/**
 * A file read ONCE and kept so it can be read again — compressed (`docs/adr/9073`).
 *
 * The Excel workbook cannot be written in one pass over its rows. The header of a sheet comes
 * before its rows and says the currency the rows turned out to share; the «Tecnico» sheet's columns
 * are known only once every row has been seen; the summary opens the workbook and counts them all.
 * So the writer reads the rows more than once, and a row source that is the network cannot be read
 * twice: the platform's streamed file of a large run takes three minutes to arrive.
 *
 * The spool is what lets the file arrive once. Each chunk is kept as it comes — gzip-compressed by
 * the browser itself (`CompressionStream`, off the main thread), so 660 MB of CSV text is a few tens
 * of megabytes in memory — and {@link ByteSpool.open} gives it back, decompressed, as often as it is
 * asked for. Where a browser has no `CompressionStream` the chunks are kept as they are: bytes, never
 * a string, so nothing is lost but the saving.
 */

export interface ByteSpool {
  /** Keeps the chunk. Resolves when the spool can take another — the backpressure of a compressing stream. */
  write(chunk: Uint8Array): Promise<void>;
  /** No more chunks. Resolves when everything written is kept. */
  close(): Promise<void>;
  /** The whole file again, from its first byte. May be called any number of times, after {@link close}. */
  open(): ReadableStream<Uint8Array>;
  /** The bytes written, uncompressed. */
  readonly bytes: number;
  /** The bytes held — what the spool costs in memory. Equal to {@link bytes} where nothing is compressed. */
  readonly stored: number;
}

function fromChunks(chunks: readonly Uint8Array[]): ReadableStream<Uint8Array> {
  let next = 0;
  return new ReadableStream<Uint8Array>({
    pull(controller) {
      const chunk = chunks[next];
      if (chunk === undefined) controller.close();
      else { next += 1; controller.enqueue(chunk); }
    },
  });
}

export function createByteSpool(): ByteSpool {
  const compressing = typeof CompressionStream === 'function' && typeof DecompressionStream === 'function';
  const kept: Uint8Array[] = [];
  let bytes = 0;
  if (!compressing) {
    return {
      write: (chunk) => { kept.push(chunk); bytes += chunk.length; return Promise.resolve(); },
      close: () => Promise.resolve(),
      open: () => fromChunks(kept),
      get bytes() { return bytes; },
      get stored() { return bytes; },
    };
  }
  const gzip = new CompressionStream('gzip');
  const writer = gzip.writable.getWriter();
  const reader = gzip.readable.getReader();
  const draining = (async (): Promise<void> => {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) return;
      kept.push(value);
    }
  })();
  // A rejection nobody has awaited yet is not an unhandled one: `close` awaits it and states it.
  draining.catch(() => undefined);
  return {
    async write(chunk) {
      bytes += chunk.length;
      await writer.write(chunk as Uint8Array<ArrayBuffer>);
    },
    async close() {
      await writer.close();
      await draining;
    },
    open: () => fromChunks(kept).pipeThrough(new DecompressionStream('gzip') as unknown as ReadableWritablePair<Uint8Array, Uint8Array>),
    get bytes() { return bytes; },
    get stored() { return kept.reduce((sum, chunk) => sum + chunk.length, 0); },
  };
}
